import React, { useState } from 'react';
import { api } from '../hooks/useApi';
import Icon from './Icon';

const TYPES = [
  { value: 'incident', label: 'Incident / déviation' },
  { value: 'note_corrective', label: 'Note corrective' },
  { value: 'courbe_temperature', label: 'Courbe température (texte)' },
];

/**
 * Formulaire append-only pour ajouter une correction à un batch finalisé.
 *
 * Props :
 *   batchId  : number
 *   onSuccess : () => void  — appelé après succès
 */
export default function FormCorrectionAppend({ batchId, onSuccess }) {
  const [type, setType] = useState('incident');
  const [contenu, setContenu] = useState('');
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  };

  const submit = async () => {
    if (contenu.trim().length < 10) {
      setErreur('Le contenu doit comporter au moins 10 caractères.');
      return;
    }
    setSaving(true);
    setErreur('');
    try {
      await api.post(`/batches/${batchId}/correction`, {
        type,
        contenu: contenu.trim(),
      });
      showToast('Correction ajoutée ✓');
      setContenu('');
      setType('incident');
      // Notifier AuditTrailBatch (même page) via event
      window.dispatchEvent(new CustomEvent('audit-trail-refresh', { detail: { batchId } }));
      if (onSuccess) onSuccess();
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Erreur lors de l\'ajout de la correction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      {toast && (
        <div className="toast-success">
          <Icon name="succes" size={15} /> {toast}
        </div>
      )}

      <h4 style={{ margin: '0 0 var(--space-3) 0', fontFamily: 'Cinzel, serif', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="ajouter" size={14} /> Ajouter une correction
      </h4>
      <p className="muted" style={{ fontSize: 12, marginBottom: 'var(--space-4)' }}>
        Les corrections sont append-only : elles s'ajoutent à l'historique sans modifier les
        données originales du batch.
      </p>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '0 0 200px' }}>
          <label>Type de correction</label>
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            disabled={saving}
          >
            {TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label>Contenu de la correction *</label>
          <textarea
            rows={3}
            placeholder="Décrivez l'incident, la correction apportée ou les données de température..."
            value={contenu}
            onChange={e => { setContenu(e.target.value); setErreur(''); }}
            disabled={saving}
            style={{ resize: 'vertical' }}
          />
        </div>
      </div>

      {erreur && (
        <div className="error-inline" style={{ marginTop: 'var(--space-2)' }}>
          <Icon name="erreur" size={14} /> {erreur}
        </div>
      )}

      <div style={{ marginTop: 'var(--space-3)', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={saving || contenu.trim().length < 10}
        >
          {saving
            ? <><Icon name="rafraichir" size={14} /> Ajout en cours...</>
            : <><Icon name="ajouter" size={14} /> Ajouter la correction</>
          }
        </button>
      </div>
    </div>
  );
}
